import { func, instanceOf, oneOfType, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

/**
 * Gets the amount of seconds since the start of a stream and the current time
 * @param {Date} startDate
 */
function currentTime(startDate) {
  return (new Date().getTime() - startDate.getTime()) / 1000;
}

function formatDuration(seconds, guide = seconds) {
  const gh = Math.floor(guide / 3600);
  const gm = Math.floor((guide / 60) % 60);

  if (isNaN(seconds) || seconds === Infinity) {
    return `${gh > 0 ? '-:' : ''}-:-`;
  }

  seconds = Math.max(seconds, 0);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds / 60) % 60);
  const s = Math.floor(seconds % 60);

  const showHours = h > 0 || gh > 0;
  const mNeedsPadding = showHours || gm >= 10;

  return `${showHours ? `${h}:` : ''}${mNeedsPadding && m < 10 ? `0${m}` : m}:${s < 10 ? `0${s}` : s}`;
}

/**
 * Used to display the time a stream has been live
 * This is designed to work with videojs only
 * @type {React.FC<{ startDate: Date | string, onClick: (e: MouseEvent) => void }>}
 */
const TimeDisplay = ({ startDate, onClick }) => {
  const start = useMemo(() => {
    if (!startDate) return new Date();
    return typeof startDate === 'string' ? new Date(startDate) : startDate;
  }, [startDate]);
  const [timeLive, setTimeLive] = useState(currentTime(start));

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        setTimeLive(currentTime(start));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <span className="op-live-text custom" onClick={onClick}>
      {formatDuration(timeLive)}
    </span>
  );
};

TimeDisplay.propTypes = {
  startDate: oneOfType([instanceOf(Date), string]).isRequired,
  onClick: func.isRequired,
};

export default TimeDisplay;
